import { Check, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { SalesOrderWorkOrder } from './types';

interface SalesOrderDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderNumber: string;
  customer: string;
  location?: string;
  date?: string;
  workOrders: SalesOrderWorkOrder[];
  onOpenWorkOrder?: (wo: SalesOrderWorkOrder) => void;
}

const STAGE_LABELS: Array<{ key: keyof Pick<SalesOrderWorkOrder, 'kittingComplete' | 'fabricComplete' | 'metalComplete' | 'finalAssemblyComplete'>; label: string; tks0026: boolean }> = [
  { key: 'kittingComplete', label: 'Kit', tks0026: false },
  { key: 'fabricComplete', label: 'Fab', tks0026: false },
  { key: 'metalComplete', label: 'Metal', tks0026: true },
  { key: 'finalAssemblyComplete', label: 'Asm', tks0026: true },
];

export function SalesOrderDetailDialog({
  open,
  onOpenChange,
  orderNumber,
  customer,
  location,
  date,
  workOrders,
  onOpenWorkOrder,
}: SalesOrderDetailDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] p-0 gap-0">
        <DialogHeader className="px-5 pt-4 pb-2.5">
          <DialogTitle className="text-base flex items-baseline flex-wrap gap-x-2 gap-y-0.5">
            <span className="font-semibold text-slate-900">{orderNumber}</span>
            <span className="text-slate-500 font-normal text-sm">· {customer}</span>
          </DialogTitle>
          <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2.5 gap-y-0.5 mt-0.5">
            {location && <span>{location}</span>}
            {date && (
              <>
                {location && <span>·</span>}
                <span>{date}</span>
              </>
            )}
            <span>·</span>
            <span>
              {workOrders.length} work order{workOrders.length === 1 ? '' : 's'}
            </span>
          </div>
        </DialogHeader>

        <div className="px-4 pt-2 pb-4 overflow-y-auto">
          {workOrders.length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-16">
              No work order details available for this sales order.
            </div>
          ) : (
            <div className="space-y-1.5">
              {workOrders.map((wo) => (
                <WorkOrderRow
                  key={wo.id}
                  wo={wo}
                  onOpen={onOpenWorkOrder ? () => onOpenWorkOrder(wo) : undefined}
                />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WorkOrderRow({
  wo,
  onOpen,
}: {
  wo: SalesOrderWorkOrder;
  onOpen?: () => void;
}) {
  const stages = STAGE_LABELS.filter((s) => !wo.isTKS0026 || s.tks0026);
  const dim =
    wo.config.width && wo.config.height
      ? `${wo.config.width}" × ${wo.config.height}"`
      : '';

  return (
    <div
      className={cn(
        'border border-slate-200 rounded-md px-3 py-2 flex items-center gap-3',
        onOpen && 'hover:bg-slate-50 cursor-pointer'
      )}
      onClick={onOpen}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm text-slate-900">{wo.workOrderId}</span>
          {wo.config.unitName && (
            <span className="text-xs text-slate-500 truncate">{wo.config.unitName}</span>
          )}
          {dim && <span className="text-xs text-slate-500">{dim}</span>}
          {wo.isTKS0026 && (
            <span className="inline-flex items-center px-1.5 py-0 rounded text-[10px] font-medium border bg-violet-50 text-violet-700 border-violet-200">
              TKS-0026
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <StatusBadge status={wo.status} />
          {wo.config.fabricType && (
            <span className="text-[11px] text-slate-500 truncate">{wo.config.fabricType}</span>
          )}
          {wo.config.frameColor && (
            <span className="text-[11px] text-slate-500 truncate">· {wo.config.frameColor}</span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1">
        {stages.map((s) => (
          <StageChip
            key={s.key}
            label={s.label}
            done={wo[s.key]}
          />
        ))}
      </div>

      {onOpen && <ExternalLink className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
    </div>
  );
}

function StatusBadge({ status }: { status: SalesOrderWorkOrder['status'] }) {
  const classes =
    status === 'Built'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : status === 'Ready'
        ? 'bg-blue-50 text-blue-700 border-blue-200'
        : status === 'Closed Unbuilt'
          ? 'bg-slate-100 text-slate-500 border-slate-200'
          : 'bg-amber-50 text-amber-700 border-amber-200';
  return (
    <span
      className={cn(
        'inline-flex items-center px-1.5 py-0 rounded text-[10px] font-medium border',
        classes
      )}
    >
      {status}
    </span>
  );
}

function StageChip({ label, done }: { label: string; done: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium border',
        done
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
          : 'bg-slate-50 text-slate-400 border-slate-200'
      )}
    >
      {done && <Check className="w-2.5 h-2.5" />}
      {label}
    </span>
  );
}
